import { AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * The response envelope the backend wraps every payload in. Services read
 * `res.data.data` for reads and `res.data` for writes, so sample responses
 * have to carry the same two levels.
 */
export interface TourApiEnvelope<T = unknown> {
  data: T;
  message: string;
  succeeded: boolean;
  statusCode: number;
}

export const tourEnvelope = <T>(
  data: T,
  message = 'Success'
): TourApiEnvelope<T> => ({
  data,
  message,
  succeeded: true,
  statusCode: 200,
});

/** Build the AxiosResponse a resolver hands back in place of a real one. */
export const tourResponse = <T>(
  config: AxiosRequestConfig,
  data: T,
  message?: string
): AxiosResponse<TourApiEnvelope<T>> => ({
  data: tourEnvelope(data, message),
  status: 200,
  statusText: 'OK',
  headers: {},
  config: config as AxiosResponse['config'],
});

/**
 * A 404 for a sample lookup that found nothing. Per the Help & Tours contract
 * the tour never falls back to the live API — a value outside the sample set
 * simply has no result, exactly as an unmatched live search would.
 */
export const tourNotFound = (
  config: AxiosRequestConfig,
  message = 'No sample result found.'
) => {
  const error = new Error(message) as Error & {
    config?: unknown;
    response?: unknown;
    isTourSampleMiss?: boolean;
    skipErrorToast?: boolean;
  };
  error.config = config;
  error.isTourSampleMiss = true;
  error.skipErrorToast = true;
  error.response = {
    data: { data: null, message, succeeded: false, statusCode: 404 },
    status: 404,
    statusText: 'Not Found',
    headers: {},
    config,
  };
  return error;
};
